import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { toast } from 'react-toastify';
import type {
  EstateAvailability,
  FlatfinderData,
  FlatfinderEstateInput,
} from 'realitiq-utils/src/flatfinder';
import { isEstateAvailabilityCompatible } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { TextArea } from '@/components/ui/text-area';
import {
  getFlatfinderAvailabilityOptions,
  getFlatfinderAvailabilityLabel,
  getFlatfinderLanguage,
} from './display';
import { FlatfinderDigitalTwinLinkField } from './digital-twin-link-field';

export function FlatfinderEstateForm({
  data,
  initial,
  estateId,
  busy,
  onSave,
  onCancel,
}: {
  data: FlatfinderData;
  initial?: FlatfinderEstateInput;
  estateId: string | null;
  busy: boolean;
  onSave: (estate: FlatfinderEstateInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const language = getFlatfinderLanguage();
  const [value, setValue] = useState<FlatfinderEstateInput>(() => ({
    name: initial?.name ?? '',
    topNumber: initial?.topNumber ?? null,
    availability: initial?.availability ?? null,
    marketType: initial?.marketType ?? data.project.marketType,
    currency: initial?.currency ?? 'EUR',
    price: initial?.price ?? null,
    livingArea: initial?.livingArea ?? null,
    rooms: initial?.rooms ?? null,
    floor: initial?.floor ?? null,
    description: initial?.description ?? null,
    digitalTwinLinkToken: initial?.digitalTwinLinkToken ?? null,
    externalLink: initial?.externalLink ?? null,
    buildingId: initial?.buildingId ?? null,
  }));
  function setField<K extends keyof FlatfinderEstateInput>(
    field: K,
    next: FlatfinderEstateInput[K]
  ) {
    setValue((current) => ({ ...current, [field]: next }));
  }
  return (
    <form
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      onSubmit={(event) => {
        event.preventDefault();
        if (!value.name.trim()) {
          toast.error(t('flatfinderFormEnterApartmentName'));
          return;
        }
        if (!isEstateAvailabilityCompatible(value.availability, value.marketType)) {
          toast.error(t('flatfinderFormChooseAvailability'));
          return;
        }
        onSave({ ...value, name: value.name.trim() });
      }}
    >
      <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-1 pb-4 [scrollbar-gutter:stable]">
        <fieldset
          disabled={busy}
          className="grid grid-cols-1 gap-x-4 gap-y-5 disabled:opacity-60 sm:grid-cols-2"
        >
          <FlatfinderDigitalTwinLinkField
            disabled={busy}
            estateId={estateId}
            value={value.digitalTwinLinkToken}
            onChange={(digitalTwinLinkToken) =>
              setField('digitalTwinLinkToken', digitalTwinLinkToken)
            }
          />
          <Input
            autoFocus
            label={t('flatfinderFormUnitLabel')}
            required
            maxLength={200}
            value={value.name}
            onChange={(event) => setField('name', event.target.value)}
          />
          <Input
            label={t('unitNumber')}
            value={value.topNumber ?? ''}
            onChange={(event) => setField('topNumber', event.target.value || null)}
          />
          <Select
            label={t('marketingType')}
            wrapperClassName="min-h-[81px]"
            value={value.marketType}
            onChange={({ target }) => {
              const marketType = target.value as FlatfinderEstateInput['marketType'];
              setValue((current) => ({
                ...current,
                marketType,
                availability: isEstateAvailabilityCompatible(current.availability, marketType)
                  ? current.availability
                  : null,
              }));
            }}
            options={[
              { id: 'buy', title: t('purchase') },
              { id: 'rent', title: t('rent') },
              { id: 'lease', title: t('lease') },
            ]}
            disableClear
          />
          <Select
            label={t('availability')}
            wrapperClassName="min-h-[81px]"
            value={value.availability ?? ''}
            onChange={({ target }) =>
              setField('availability', (target.value || null) as EstateAvailability)
            }
            options={[
              { id: '', title: getFlatfinderAvailabilityLabel(null, language) },
              ...getFlatfinderAvailabilityOptions(language, [value.marketType]),
            ]}
            disableClear
          />
          <Input
            label={value.marketType === 'buy' ? t('purchasePriceSection') : t('monthlyRent')}
            type="number"
            min="0"
            step="0.01"
            value={value.price ?? ''}
            onChange={(event) =>
              setField('price', event.target.value === '' ? null : Number(event.target.value))
            }
          />
          <Input
            label={t('currency')}
            required
            pattern="[A-Za-z]{3}"
            maxLength={3}
            value={value.currency}
            onChange={(event) => setField('currency', event.target.value.toUpperCase())}
          />
          <Input
            label={t('livingArea')}
            type="number"
            min="0"
            step="0.01"
            value={value.livingArea ?? ''}
            onChange={(event) =>
              setField('livingArea', event.target.value === '' ? null : Number(event.target.value))
            }
          />
          <Input
            label={t('rooms')}
            type="number"
            min="0"
            step="1"
            value={value.rooms ?? ''}
            onChange={(event) =>
              setField('rooms', event.target.value === '' ? null : Number(event.target.value))
            }
          />
          <Input
            label={t('floor')}
            type="number"
            step="1"
            value={value.floor ?? ''}
            onChange={(event) =>
              setField('floor', event.target.value === '' ? null : Number(event.target.value))
            }
          />
          <Select
            label={t('building')}
            wrapperClassName="min-h-[81px]"
            value={value.buildingId ?? ''}
            onChange={({ target }) => setField('buildingId', target.value || null)}
            options={[
              { id: '', title: t('projectAddress') },
              ...data.project.buildings.map((building) => ({
                id: building.id,
                title: building.name,
              })),
            ]}
            disableClear
          />
          <TextArea
            label={t('description')}
            rows={3}
            value={value.description ?? ''}
            onChange={(event) => setField('description', event.target.value || null)}
            className="min-h-24"
            wrapperClassName="sm:col-span-2"
          />
          <Input
            label={t('moreInformationUrl')}
            type="url"
            value={value.externalLink ?? ''}
            onChange={(event) => setField('externalLink', event.target.value || null)}
            wrapperClassName="sm:col-span-2"
          />
        </fieldset>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t bg-primary-lighter px-1 pt-4">
        <Button type="button" disabled={busy} onClick={onCancel} variant="outline">
          {t('cancel')}
        </Button>
        <Button type="submit" disabled={busy} loading={busy}>
          {busy ? t('saving') : initial ? t('saveApartment') : t('createApartment')}
        </Button>
      </div>
    </form>
  );
}
